import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../../core/api/client';
import { ServerErrorPage } from '../../public/pages/ServerErrorPage';

import { FeaturedProjectsSlider } from './FeaturedProjectsSlider';
import { LocationsSection } from './LocationsSection';
import { FeaturedUnitsSlider } from './FeaturedUnitsSlider';
import { HeroSlider } from './HeroSlider';
import { TrustSection } from './TrustSection';
import { HeroSection } from './HeroSection';

import { DiscoveryHeader } from './DiscoveryHeader';
import { FeaturedDestinations } from './FeaturedDestinations';
import { SmartCollections } from './SmartCollections';
import { MapPreview } from './MapPreview';
import { ExpertPick } from './ExpertPick';
import { ActivityFeed } from './ActivityFeed';

// Component Map
const SECTION_COMPONENTS: Record<string, any> = {
    'hero_section': HeroSection,
    'hero_slider': HeroSlider,
    'featured_projects': FeaturedProjectsSlider,
    'locations_section': LocationsSection,
    'featured_units': FeaturedUnitsSlider,
    'trust_section': TrustSection,
    // New Explore Components
    'discovery_header': DiscoveryHeader,
    'featured_destinations': FeaturedDestinations,
    'smart_collections': SmartCollections,
    'map_preview': MapPreview,
    'expert_pick': ExpertPick,
    'activity_feed': ActivityFeed,
};

interface Section {
    id: string;
    type: string;
    isVisible: boolean;
    config: any;
}

interface PageRendererProps {
    slug: string;
}

const fetchPage = async (slug: string) => {
    // The api client handles the base URL and localization headers
    const { data } = await api.get<any>(`/api/pages/${slug}`);
    return data ?? null;
};

export const PageRenderer = ({ slug }: PageRendererProps) => {
    const { i18n } = useTranslation();

    // Fetch page data (including sections)
    const { data: page, isLoading, error, refetch } = useQuery({
        queryKey: ['page', slug, i18n.language],
        queryFn: () => fetchPage(slug),
        retry: false
    });

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-slate-200 border-t-primary rounded-full animate-spin" />
                    <p className="text-slate-500 font-light animate-pulse">Loading {slug}...</p>
                </div>
            </div>
        );
    }

    if (error || !page) {
        return <ServerErrorPage withLayout={false} retry={refetch} />;
    }

    const sections: Section[] = page.sections || [];

    // Sort by order if needed, but array order in JSON should be preserved.
    // Filter visible sections
    const visibleSections = sections.filter(s => s.isVisible);

    return (
        <div className="flex flex-col w-full">
            {visibleSections.map(section => {
                const Component = SECTION_COMPONENTS[section.type];
                if (!Component) {
                    console.warn(`Unknown section type: ${section.type}`);
                    return null;
                }
                return (
                    <div key={section.id} id={section.id}>
                        <Component config={section.config} />
                    </div>
                );
            })}
        </div>
    );
};
